import React, { useState, useMemo, useEffect } from 'react';
import { X, MessageCircle, Minus, Plus, MapPin, Calendar as CalIcon, Users, User, Phone } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { trackLead } from '../utils/trackLead';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * EnquireModal v3 — editorial guided enquiry.
 *
 * Key UX fixes vs v2:
 *  - Dates card shows Check-in | Nights | Check-out as 3 zones so guests
 *    understand they pick TWO dates (the nights are the days BETWEEN).
 *  - Helper sentence above the calendar: "Tap your check-in, then check-out."
 *  - Optional "Your name" field — host gets a personable enquiry.
 *  - WhatsApp message rebuilt as clean labeled rows (no emoji-stacking).
 *  - Estimate parses startingPrice as a number (was breaking on "5,500" string).
 */
export default function EnquireModal({
  open,
  onClose,
  onSent = null, // optional callback(enquiryData) fired after WA send; if provided, parent owns close UX
  stay,
  whatsappNumber,
  startingPrice = null,
  heroImage = null,
  source = 'collective',
  contextLabel = 'enquire_modal',
  propertyShortName = '',
  // Collection mode: when set, the modal is opened from a /collection/<slug>
  // page. The 10% discount path is suppressed entirely (GoHimalayan acts as
  // the support layer + earns commission on these larger bookings), and the
  // contact details — name, city, phone — become mandatory. The message
  // header reads "Collection enquiry" and includes the collection title.
  collectionMode = false,
  collectionName = '',
}) {
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestCity, setGuestCity] = useState('');
  // In collection mode the discount path doesn't exist; otherwise it's
  // pre-checked because it's a no-brainer for individual enquiries.
  const [claimDiscount, setClaimDiscount] = useState(!collectionMode);
  const [issuingCode, setIssuingCode] = useState(false);
  const [range, setRange] = useState(undefined);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [rooms, setRooms] = useState(1);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);

  useEffect(() => {
    if (!open) { setError(''); setShowCalendar(false); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);

  const nights = useMemo(() => {
    if (!range?.from || !range?.to) return 0;
    return Math.max(0, Math.round((range.to - range.from) / 86400000));
  }, [range]);

  const totalGuests = adults + children;

  // Defensive numeric parse — Bluehouse's `startingPrice` is the string "5,500"
  // so a naïve multiplication returned NaN in the summary chip.
  const priceNum = useMemo(() => {
    if (startingPrice == null || startingPrice === '') return null;
    const n = Number(String(startingPrice).replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [startingPrice]);

  const estTotal = priceNum && nights > 0 && rooms > 0 ? priceNum * nights * rooms : null;

  const fmt = (d) => d ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const fmtShort = (d) => d ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '';
  const fmtDay = (d) => d ? d.toLocaleDateString('en-IN', { weekday: 'short' }) : '';
  const toISO = (d) => {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  if (!open) return null;

  const handleSend = async () => {
    setError('');
    if (!range?.from || !range?.to) { setError('Please pick your dates to continue.'); setShowCalendar(true); return; }
    if (nights <= 0) { setError('Check-out must be at least one day after check-in.'); setShowCalendar(true); return; }
    if (adults < 1) { setError('At least 1 adult is needed.'); return; }
    // Collection mode → name + city + phone all mandatory so GoHimalayan can
    // route the enquiry as the support layer between guest and host.
    if (collectionMode) {
      if (!guestName.trim())  { setError('Please add your name.');  return; }
      if (!guestCity.trim())  { setError('Please add your city.');  return; }
      if (!guestPhone.trim()) { setError('Please add your phone (WhatsApp).'); return; }
    }
    if (!collectionMode && claimDiscount && !guestPhone.trim()) {
      setError('Phone is needed to apply your 10% discount. Untick the discount box to skip.');
      return;
    }

    const propName = stay?.display_name?.split(',')[0] || propertyShortName || 'your property';
    const valley = stay?.valley || '';
    const guestsLine = children > 0
      ? `${adults} ${adults === 1 ? 'adult' : 'adults'} + ${children} ${children === 1 ? 'child' : 'children'} (${totalGuests} total)`
      : `${adults} ${adults === 1 ? 'adult' : 'adults'}`;
    const pageUrl = typeof window !== 'undefined' ? window.location.href.split('?')[0] : '';

    // Normalise phone: digits only, India default 91 if user typed 10 digits.
    const phoneClean = guestPhone.replace(/[^0-9]/g, '');
    const phoneFinal = phoneClean.length === 10 ? `91${phoneClean}` : phoneClean;

    // If guest opted in for the discount (single-stay flow only), issue a
    // coupon code BEFORE opening WhatsApp so the host sees the code embedded
    // in the enquiry. Collection mode never issues coupons — GoHimalayan
    // earns commission on these directly from the host.
    let couponCode = null;
    if (!collectionMode && claimDiscount && phoneFinal) {
      setIssuingCode(true);
      try {
        const res = await fetch(`${API}/api/coupons/issue`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            path: 'A',
            stay_id: stay?.gohimalayan_id || null,
            stay_name: stay?.display_name || null,
            page: typeof window !== 'undefined' ? window.location.pathname : '',
            guest_phone: phoneFinal,
            guest_name: guestName.trim() || null,
            guest_city: guestCity.trim() || null,
          }),
        });
        const data = await res.json();
        if (res.ok) couponCode = data.code;
      } catch { /* if it fails we just skip the code line */ }
      setIssuingCode(false);
    }

    // Clean, label-aligned WhatsApp message — easy for the recipient to scan.
    // Collection mode swaps the property line for the collection title so the
    // GoHimalayan central inbox treats this as a "support / large booking"
    // enquiry (not a single-stay booking).
    const headerLine = collectionMode
      ? (guestName.trim()
          ? `Hi GoHimalayan, ${guestName.trim()} here — I'm enquiring about ${collectionName || 'a collection'}.`
          : `Hi GoHimalayan — I'm enquiring about ${collectionName || 'a collection'}.`)
      : (guestName.trim()
          ? `Hi, ${guestName.trim()} here — I'd like to enquire about your stay.`
          : 'Hi, I’d like to enquire about your stay.');
    const lines = [
      headerLine,
      '',
    ];
    if (collectionMode) {
      lines.push(`Collection: ${collectionName || '—'}`);
    } else {
      lines.push(`Property: ${propName}${valley ? ` (${valley})` : ''}`);
    }
    lines.push(
      `Check-in: ${fmt(range.from)}`,
      `Check-out: ${fmt(range.to)}`,
      `Total nights: ${nights}`,
      `Guests: ${guestsLine}`,
      `Rooms: ${rooms}`,
    );
    if (guestCity.trim()) lines.push(`City: ${guestCity.trim()}`);
    if (phoneFinal) lines.push(`Phone: +${phoneFinal}`);
    if (note.trim()) lines.push(`Notes: ${note.trim()}`);
    if (couponCode) {
      lines.push('');
      lines.push(`🎁 GoHimalayan 10% discount: ${couponCode}`);
      lines.push('(Please apply 10% off the agreed nightly rate — both of us save vs. OTA commissions.)');
    }
    lines.push('');
    lines.push('Please share availability & total cost.');
    lines.push('');
    lines.push(`— Sent via GoHimalayan · ${pageUrl}`);

    const message = lines.join('\n');

    trackLead({
      stay,
      source,
      context: contextLabel,
      enquiry: {
        check_in: toISO(range.from),
        check_out: toISO(range.to),
        nights,
        adults,
        children,
        rooms,
        note: note.trim() || null,
        guest_name: guestName.trim() || null,
        guest_phone: phoneFinal || null,
        guest_city: guestCity.trim() || null,
        coupon_code: couponCode,
      },
    });

    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    if (onSent) {
      onSent({ coupon_code: couponCode });
    } else {
      onClose();
    }
  };

  const propName = stay?.display_name?.split(',')[0] || propertyShortName || 'this property';
  const playfair = { fontFamily: "'Playfair Display', Georgia, serif" };

  const dayPickerStyles = `
    .rdp { --rdp-cell-size: 32px; --rdp-accent-color: #2C3E2D; margin: 0; padding: 4px; }
    .rdp-months { justify-content: center; }
    .rdp-month { margin: 0; }
    .rdp-caption { padding: 0 4px 4px; }
    .rdp-caption_label { font-family: 'Playfair Display', Georgia, serif; font-size: 13px; color: #1a2a1c; font-weight: 500; }
    .rdp-nav_button { height: 24px; width: 24px; color: #2C3E2D; }
    .rdp-head_cell { font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: #a8a29e; font-weight: 600; height: 22px; }
    .rdp-tbody { font-size: 12px; }
    .rdp-day { border-radius: 50%; transition: all 0.12s ease; font-size: 12px; height: 32px; width: 32px; }
    .rdp-day:hover:not([disabled]):not(.rdp-day_selected) { background: #ecede4; }
    .rdp-day_today:not(.rdp-day_selected) { color: #2C3E2D; font-weight: 700; background: transparent; }
    .rdp-day_selected, .rdp-day_selected:hover { background: #2C3E2D !important; color: white !important; }
    .rdp-day_range_middle { background: #ecede4 !important; color: #2C3E2D !important; border-radius: 0 !important; }
    .rdp-day_range_start { border-radius: 50% 0 0 50% !important; }
    .rdp-day_range_end { border-radius: 0 50% 50% 0 !important; }
    .rdp-day_range_start.rdp-day_range_end { border-radius: 50% !important; }
    .rdp-day_disabled { opacity: 0.22; }
  `;

  // Dates display has 3 distinct slots: Check-in | Nights | Check-out
  const hasIn = !!range?.from;
  const hasOut = !!range?.to;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-stone-900/70 backdrop-blur-md animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="enquire-modal-backdrop"
    >
      <style>{dayPickerStyles}</style>
      <div
        className="bg-[#faf8f4] w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl shadow-2xl max-h-[94vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom duration-300"
        onClick={(e) => e.stopPropagation()}
        data-testid="enquire-modal"
        role="dialog"
        aria-modal="true"
      >
        {/* Mobile drag handle */}
        <div className="sm:hidden flex justify-center pt-2.5 pb-1">
          <div className="w-10 h-1 rounded-full bg-stone-300" />
        </div>

        {/* Editorial header — compact */}
        <div className="px-4 pt-3 sm:pt-4 pb-3 flex items-center gap-3 border-b border-stone-200/70">
          {heroImage && (
            <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 ring-1 ring-stone-200">
              <img src={heroImage} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[9px] tracking-[0.3em] uppercase text-[#2C3E2D]/60 font-semibold mb-0.5">Enquire</p>
            <h2 className="text-base leading-tight text-stone-900 truncate" style={playfair}>{propName}</h2>
            {stay?.valley && (
              <p className="text-[10px] text-stone-500 flex items-center gap-1 mt-0.5">
                <MapPin size={9} strokeWidth={1.5} /> {stay.valley}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-stone-200/60 rounded-full text-stone-500 hover:text-stone-900 transition flex-shrink-0"
            aria-label="Close enquiry"
            data-testid="enquire-modal-close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Scrollable body — tightened to minimise scroll on mobile */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {/* Dates — 3-zone display + collapsible calendar */}
          <section>
            <button
              type="button"
              onClick={() => setShowCalendar((s) => !s)}
              className={`w-full text-left rounded-xl border-2 transition-all bg-white p-0 overflow-hidden ${
                hasIn && hasOut ? 'border-[#2C3E2D]' : 'border-stone-200 hover:border-stone-400'
              }`}
              data-testid="enquire-dates-toggle"
            >
              <div className="grid grid-cols-[1fr_auto_1fr] items-stretch">
                <DateSlot label="Check-in" date={range?.from} fmtShort={fmtShort} fmtDay={fmtDay} />
                <div className="flex flex-col items-center justify-center px-2 py-2 border-x border-stone-100 bg-stone-50/40 min-w-[64px]">
                  <p className={`text-[10px] tracking-[0.2em] uppercase font-semibold ${nights > 0 ? 'text-[#2C3E2D]' : 'text-stone-400'}`}>
                    {nights > 0 ? `${nights} ${nights === 1 ? 'night' : 'nights'}` : 'Nights'}
                  </p>
                  <CalIcon size={13} className={`mt-0.5 ${nights > 0 ? 'text-[#2C3E2D]' : 'text-stone-300'}`} strokeWidth={1.5} />
                </div>
                <DateSlot label="Check-out" date={range?.to} fmtShort={fmtShort} fmtDay={fmtDay} align="right" />
              </div>
              <div className="bg-stone-50 px-3 py-1 flex items-center justify-between border-t border-stone-100">
                <span className="text-[10px] text-stone-500">
                  {hasIn && hasOut ? 'Tap to change' : hasIn ? 'Now pick check-out →' : 'Tap to pick your dates'}
                </span>
                <span className="text-[10px] text-[#2C3E2D] font-semibold tracking-wider uppercase">{showCalendar ? 'Close' : 'Pick dates'}</span>
              </div>
            </button>

            {showCalendar && (
              <div className="mt-2" data-testid="enquire-calendar-wrap">
                <p className="text-[10px] text-stone-500 px-1 mb-1.5 leading-snug">
                  Tap <b className="text-stone-700">check-in</b>, then <b className="text-stone-700">check-out</b> — days between = nights.
                </p>
                <div className="bg-white rounded-xl border border-stone-200 py-1 px-1 flex justify-center">
                  <DayPicker
                    mode="range"
                    selected={range}
                    onSelect={setRange}
                    disabled={{ before: today }}
                    fromMonth={today}
                    numberOfMonths={1}
                    showOutsideDays={false}
                  />
                </div>
              </div>
            )}
          </section>

          {/* Guests + Rooms — compact */}
          <section className="bg-white rounded-xl border border-stone-200 px-4 py-2.5">
            <div className="flex items-center gap-2 mb-1">
              <Users size={13} className="text-[#2C3E2D]/70" strokeWidth={1.5} />
              <p className="text-[10px] tracking-[0.25em] uppercase text-stone-500 font-semibold">Guests & rooms</p>
            </div>
            <div className="divide-y divide-stone-100">
              <Stepper label="Adults" sublabel="Age 13+" value={adults} setValue={setAdults} min={1} max={20} testid="adults" />
              <Stepper label="Children" sublabel="Age 0–12" value={children} setValue={setChildren} min={0} max={10} testid="children" />
              <Stepper label="Rooms / cottages" value={rooms} setValue={setRooms} min={1} max={10} testid="rooms" />
            </div>
          </section>

          {/* Contact details. Layout depends on which flow we're in:
              • single stay      → 10% discount card with name/city/phone inside
                                    (the discount is the carrot for filling them)
              • collection enquiry → name/city/phone are ALL mandatory, shown
                                    in a clean white card without the carrot.
                                    GoHimalayan acts as the support layer + earns
                                    commission on these bookings directly. */}
          {collectionMode ? (
            <section
              className="rounded-xl px-4 py-3 bg-white border-2 border-stone-200"
              data-testid="enquire-contact-card"
            >
              <p className="text-sm font-semibold text-stone-900 leading-tight mb-0.5">
                Your details <span className="text-red-500">*</span>
              </p>
              <p className="text-[11px] text-stone-500 leading-snug mb-3">
                We&rsquo;ll WhatsApp you a curated shortlist within the day.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div className="relative">
                  <User size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" strokeWidth={1.5} />
                  <input
                    type="text"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder="Your name *"
                    maxLength={60}
                    className="w-full pl-8 pr-2 py-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-stone-900 placeholder:text-stone-400"
                    data-testid="enquire-guest-name"
                    required
                  />
                </div>
                <div className="relative">
                  <MapPin size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" strokeWidth={1.5} />
                  <input
                    type="text"
                    value={guestCity}
                    onChange={(e) => setGuestCity(e.target.value)}
                    placeholder="City *"
                    maxLength={60}
                    className="w-full pl-8 pr-2 py-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-stone-900 placeholder:text-stone-400"
                    data-testid="enquire-guest-city"
                    required
                  />
                </div>
              </div>
              <div className="relative mt-2.5">
                <Phone size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" strokeWidth={1.5} />
                <input
                  type="tel"
                  value={guestPhone}
                  onChange={(e) => setGuestPhone(e.target.value)}
                  placeholder="+91 98xxx xxxxx (WhatsApp) *"
                  maxLength={20}
                  inputMode="tel"
                  className="w-full pl-8 pr-2 py-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-stone-900 placeholder:text-stone-400"
                  data-testid="enquire-guest-phone"
                  required
                />
              </div>
            </section>
          ) : (
          /* 10% guest discount — single-stay flow. Tick box on by default. */
          <section
            className={`rounded-xl px-4 py-3 border-2 transition-colors ${claimDiscount ? 'bg-amber-50/70 border-amber-300' : 'bg-white border-stone-200'}`}
            data-testid="enquire-discount-card"
          >
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={claimDiscount}
                onChange={(e) => setClaimDiscount(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-stone-400 text-amber-600 focus:ring-amber-500 cursor-pointer"
                data-testid="enquire-discount-checkbox"
              />
              <div className="flex-1">
                <p className="text-sm font-semibold text-stone-900 leading-tight">
                  Apply <span className="text-amber-700">10% off</span> this stay
                </p>
                <p className="text-[11px] text-stone-600 leading-snug mt-0.5">
                  We send a discount code with your enquiry — host applies 10% off the agreed rate.
                  <br />Both you & the host save vs. OTA commissions.
                </p>
              </div>
            </label>

            {claimDiscount && (
              <div className="mt-3 pt-3 border-t border-amber-200/60 space-y-2.5" data-testid="enquire-discount-fields">
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <User size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" strokeWidth={1.5} />
                    <input
                      type="text"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder="Your name"
                      maxLength={60}
                      className="w-full pl-8 pr-2 py-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-stone-900 placeholder:text-stone-400"
                      data-testid="enquire-guest-name"
                    />
                  </div>
                  <div className="relative">
                    <MapPin size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" strokeWidth={1.5} />
                    <input
                      type="text"
                      value={guestCity}
                      onChange={(e) => setGuestCity(e.target.value)}
                      placeholder="City"
                      maxLength={60}
                      className="w-full pl-8 pr-2 py-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-stone-900 placeholder:text-stone-400"
                      data-testid="enquire-guest-city"
                    />
                  </div>
                </div>
                <div className="relative">
                  <Phone size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" strokeWidth={1.5} />
                  <input
                    type="tel"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    placeholder="+91 98xxx xxxxx (WhatsApp) — required for discount"
                    maxLength={20}
                    inputMode="tel"
                    className="w-full pl-8 pr-2 py-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-stone-900 placeholder:text-stone-400"
                    data-testid="enquire-guest-phone"
                  />
                </div>
              </div>
            )}
          </section>
          )}

          {/* Optional notes — collapsed by default to keep the form short */}
          <details className="bg-white rounded-xl border border-stone-200 px-4 py-2.5 group" data-testid="enquire-details-disclosure">
            <summary className="flex items-center justify-between cursor-pointer list-none">
              <p className="text-[10px] tracking-[0.25em] uppercase text-stone-500 font-semibold">
                Add a note <span className="text-stone-400 normal-case tracking-normal font-normal">(optional)</span>
              </p>
              <span className="text-[10px] text-[#2C3E2D] font-semibold tracking-wider uppercase group-open:hidden">Add</span>
              <span className="text-[10px] text-[#2C3E2D] font-semibold tracking-wider uppercase hidden group-open:inline">Hide</span>
            </summary>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={300}
              placeholder="Anything else? e.g. vegetarian meals, ground floor, late arrival…"
              className="w-full mt-2.5 px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-[#2C3E2D] focus:bg-white resize-none placeholder:text-stone-400"
              data-testid="enquire-note"
            />
          </details>

          {error && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="enquire-error">
              {error}
            </div>
          )}

          {nights > 0 && (
            <div className="bg-[#2C3E2D]/[0.04] border border-[#2C3E2D]/15 rounded-xl px-3.5 py-2" data-testid="enquire-summary">
              <p className="text-[9px] tracking-[0.25em] uppercase text-[#2C3E2D]/70 font-semibold mb-0.5">Your enquiry</p>
              <p className="text-xs text-stone-800 leading-snug">
                <b>{nights} {nights === 1 ? 'night' : 'nights'}</b> · {fmtShort(range.from)} → {fmtShort(range.to)} · <b>{totalGuests} {totalGuests === 1 ? 'guest' : 'guests'}</b>{rooms > 1 ? ` · ${rooms} rooms` : ''}
                {estTotal && (
                  <> · from <span className="font-serif italic" style={playfair}>₹{estTotal.toLocaleString('en-IN')}</span></>
                )}
              </p>
            </div>
          )}
        </div>

        {/* Sticky CTA footer */}
        <div className="bg-[#faf8f4]/95 backdrop-blur-md border-t border-stone-200/70 px-4 py-3">
          <button
            onClick={handleSend}
            disabled={issuingCode}
            className="w-full flex items-center justify-center gap-2.5 bg-[#2C3E2D] text-white py-3.5 rounded-full text-sm font-medium hover:bg-[#1a2a1c] active:scale-[0.98] transition-all shadow-lg shadow-[#2C3E2D]/20 group disabled:opacity-70 disabled:cursor-wait"
            data-testid="enquire-send-btn"
          >
            <MessageCircle size={15} className="group-hover:rotate-[-6deg] transition-transform" />
            {issuingCode ? 'Generating discount code…' : 'Send enquiry on WhatsApp'}
          </button>
          <p className="text-[9px] text-stone-400 text-center mt-1.5 leading-relaxed">
            {collectionMode
              ? 'Goes to GoHimalayan · We curate your shortlist · Reply within 24h'
              : 'Goes directly to the host · Zero commission · Quote within 24h'}
          </p>
        </div>
      </div>
    </div>
  );
}

const DateSlot = ({ label, date, fmtShort, fmtDay, align = 'left' }) => (
  <div className={`px-3 py-2.5 flex flex-col ${align === 'right' ? 'items-end text-right' : 'items-start text-left'}`}>
    <span className="text-[9px] tracking-[0.2em] uppercase text-stone-400 font-semibold mb-0.5">{label}</span>
    {date ? (
      <>
        <span className="text-sm text-stone-900 font-medium leading-tight">{fmtShort(date)}</span>
        <span className="text-[10px] text-stone-500 mt-0.5">{fmtDay(date)}</span>
      </>
    ) : (
      <span className="text-sm text-stone-400">—</span>
    )}
  </div>
);

const Stepper = ({ label, sublabel, value, setValue, min = 0, max = 20, testid }) => (
  <div className="flex items-center justify-between py-1.5">
    <div>
      <p className="text-sm text-stone-900 font-medium leading-tight">{label}</p>
      {sublabel && <p className="text-[10px] text-stone-400 mt-0.5">{sublabel}</p>}
    </div>
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => setValue(Math.max(min, value - 1))}
        disabled={value <= min}
        className="w-7 h-7 rounded-full border border-stone-300 flex items-center justify-center text-stone-700 hover:bg-[#2C3E2D] hover:text-white hover:border-[#2C3E2D] disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-stone-700 disabled:hover:border-stone-300 transition active:scale-90"
        aria-label={`Decrease ${label}`}
        data-testid={`enquire-${testid}-minus`}
      >
        <Minus size={12} strokeWidth={2} />
      </button>
      <span className="w-6 text-center text-sm font-medium tabular-nums text-stone-900" data-testid={`enquire-${testid}-value`}>
        {value}
      </span>
      <button
        type="button"
        onClick={() => setValue(Math.min(max, value + 1))}
        disabled={value >= max}
        className="w-7 h-7 rounded-full border border-stone-300 flex items-center justify-center text-stone-700 hover:bg-[#2C3E2D] hover:text-white hover:border-[#2C3E2D] disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-stone-700 disabled:hover:border-stone-300 transition active:scale-90"
        aria-label={`Increase ${label}`}
        data-testid={`enquire-${testid}-plus`}
      >
        <Plus size={12} strokeWidth={2} />
      </button>
    </div>
  </div>
);
